import { useEffect, useState } from 'react';
import { Alert, Button, Container, Group, Paper, Select, Stack, Text, Title } from '@mantine/core';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import { getFirebaseAuth } from '../auth/firebase';
import { useAuthStore } from '../auth/authStore';
import { signOut } from '../auth/AuthProvider';
import { api } from '../api/client';

type Membership = { id: string; estate: string; tenant: string };
type Device = { id: string; label: string; estate: string; tenant: string; enabled: boolean };
type Request = { estate: string; tenant?: string; label: string; key_id: string; expires: string };
const base = '/api/v1/enrollment';

/** Ordinary Firebase users can reach this screen; every grant is checked by
 * the membership API. The operator console still has its superAdmin gate. */
export function EnrollmentPage() {
  const user = useAuthStore((s) => s.user);
  const [search] = useSearchParams();
  const requestID = search.get('request');
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [request, setRequest] = useState<Request | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    setMemberships([]); setDevices([]); setRequest(null); setSelected(null); setError('');
    if (!user) return;
    Promise.allSettled([
      api.get<Membership[]>(`${base}/memberships`),
      api.get<Device[]>(`${base}/devices`),
      requestID ? api.get<Request>(`${base}/requests/${encodeURIComponent(requestID)}`) : Promise.resolve(null),
    ]).then(([grants, rows, pending]) => {
      if (!current) return;
      if (grants.status === 'fulfilled') setMemberships(grants.value);
      if (rows.status === 'fulfilled') setDevices(rows.value ?? []);
      if (pending.status === 'fulfilled') setRequest(pending.value);
      if ([grants, rows, pending].some((r) => r.status === 'rejected')) setError('Some access details could not be loaded. The enrollment request may have expired.');
    }).catch(() => { if (current) setError('Could not load device access. The request may have expired.'); });
    return () => { current = false; };
  }, [user, requestID, revision]);
  async function enroll() {
    const grant = memberships.find((m) => m.id === selected);
    if (!user || !grant || !requestID) return;
    const subject = user.uid;
    setBusy(true); setError('');
    try {
      const result = await api.post<{ redirect: string }>(`${base}/requests/${encodeURIComponent(requestID)}/select`, { estate: grant.estate, tenant: grant.tenant });
      if (useAuthStore.getState().user?.uid !== subject) return;
      const redirect = new URL(result.redirect);
      if (redirect.protocol !== 'http:' || !['127.0.0.1', '[::1]'].includes(redirect.hostname) || redirect.pathname !== '/enrollment/callback') throw new Error('Invalid return address');
      window.location.assign(redirect.href);
    } catch { setError('Enrollment was refused or expired. Start it again from Hub.'); }
    finally { setBusy(false); }
  }
  async function revoke(id: string) {
    setBusy(true); setError('');
    try { await api.del(`${base}/devices/${encodeURIComponent(id)}`); setRevision((r) => r + 1); }
    catch { setError('Could not revoke this device.'); }
    finally { setBusy(false); }
  }
  return <Container size="sm" py="xl"><Stack gap="lg">
    <Title order={1}>Hub device access</Title>
    <Text>Each computer connects to one company. Your Google identity only grants access to companies explicitly assigned to you.</Text>
    {error && <Alert color="red">{error}</Alert>}
    {!user ? <Button onClick={async () => {
      const auth = getFirebaseAuth(); if (!auth) { setError('Google sign-in is not configured.'); return; }
      try { await signInWithPopup(auth, new GoogleAuthProvider()); } catch { setError('Google sign-in did not finish.'); }
    }}>Sign in with Google</Button> : <>
      <Group justify="space-between"><Text>{user.email}</Text><Button variant="subtle" onClick={() => void signOut()}>Sign out</Button></Group>
      {request && <Paper withBorder p="lg"><Stack>
        <Title order={2}>Authorize {request.label}</Title>
        <Alert>Continue only if you just started enrollment from Hub on this computer. Do not authorize a request sent to you by someone else.</Alert>
        <Text size="sm">Request expires: {new Date(request.expires).toLocaleString()}</Text>
        <Text size="sm">Device key: {request.key_id}</Text>
        <Select label="Company" placeholder="Choose the company for this computer" data={memberships.filter((m) => m.estate === request.estate && (!request.tenant || m.tenant === request.tenant)).map((m) => ({ value: m.id, label: `${m.estate} / ${m.tenant}` }))} value={selected} onChange={setSelected} />
        {!memberships.some((m) => m.estate === request.estate && (!request.tenant || m.tenant === request.tenant)) && <Text>No company grants are enabled. Ask a router operator to grant your Firebase subject access.</Text>}
        <Button disabled={!selected || busy} loading={busy} onClick={() => void enroll()}>Authorize this computer</Button>
      </Stack></Paper>}
      <Title order={2}>Your devices</Title>
      {devices.map((device) => <Paper withBorder p="md" key={device.id}><Group justify="space-between"><div><Text fw={600}>{device.label}</Text><Text size="sm">{device.estate} / {device.tenant}</Text><Text size="xs">{device.id}</Text></div><Button color="red" variant="light" disabled={!device.enabled || busy} onClick={() => void revoke(device.id)}>{device.enabled ? 'Revoke access' : 'Revoked'}</Button></Group></Paper>)}
    </>}
  </Stack></Container>;
}
